import React, { useEffect, useState, useCallback } from "react";
import confetti from "canvas-confetti";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PhoneCall, Trash2, Trophy, PhoneOff, CalendarClock, Send, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// Actieve uitkomsten (knoppen in de popup)
const UITKOMSTEN = [
  { value: "geen_gehoor", label: "Geen gehoor", icon: PhoneOff },
  { value: "later_terugbellen", label: "Later terugbellen", icon: CalendarClock },
  { value: "sample", label: "Sample toesturen", icon: Send },
  { value: "deal", label: "Deal!", icon: Trophy },
];
// Labels incl. historische uitkomsten (voor weergave in de lijst)
const LABELS = {
  geen_gehoor: "Geen gehoor", later_terugbellen: "Later terugbellen",
  sample: "Sample toesturen", deal: "Deal!",
  gesproken: "Gesproken", afspraak: "Afspraak gemaakt",
};
const VARIANT = {
  geen_gehoor: "outline", later_terugbellen: "default", sample: "secondary", deal: "default",
  gesproken: "secondary", afspraak: "default",
};
const BTN_CLASS = {
  geen_gehoor: "bg-muted hover:bg-muted/80 text-foreground",
  later_terugbellen: "bg-amber-500 hover:bg-amber-600 text-white",
  sample: "bg-sky-500 hover:bg-sky-600 text-white",
  deal: "bg-emerald-500 hover:bg-emerald-600 text-white",
};

const fireConfetti = () => {
  const end = Date.now() + 800;
  const colors = ["#10b981", "#008C88", "#fbbf24", "#ffffff"];
  (function frame() {
    confetti({ particleCount: 4, angle: 60, spread: 60, origin: { x: 0 }, colors });
    confetti({ particleCount: 4, angle: 120, spread: 60, origin: { x: 1 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
  confetti({ particleCount: 90, spread: 90, origin: { y: 0.6 }, colors });
};

const morgen = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); };

export function BelletjesPanel({ kansId, onLogged, open: openProp, onOpenChange }) {
  const [items, setItems] = useState([]);
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp !== undefined ? openProp : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;
  const [notitie, setNotitie] = useState("");
  const [busy, setBusy] = useState(false);
  const [stap, setStap] = useState("kies"); // "kies" | "terugbel" | "afval"
  const [datum, setDatum] = useState(morgen());
  const [tijd, setTijd] = useState("09:00");

  const load = useCallback(async () => {
    const { data } = await api.get(`/verkoopkansen/${kansId}/belletjes`);
    setItems(data);
  }, [kansId]);
  useEffect(() => { load(); }, [load]);

  const resetDialog = () => { setNotitie(""); setStap("kies"); setDatum(morgen()); setTijd("09:00"); };

  const log = async (uitkomst, extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/verkoopkansen/${kansId}/belletjes`, { uitkomst, notitie, ...extra });
      await load();
      onLogged?.();
      if (uitkomst === "deal") {
        fireConfetti();
        toast.success("Deal gewonnen! 🎉");
        setOpen(false); resetDialog();
      } else if (uitkomst === "sample") {
        toast.success("Sample-fase ingesteld");
        setOpen(false); resetDialog();
      } else if (uitkomst === "later_terugbellen") {
        toast.success("Later teruggebeld — ingepland");
        setOpen(false); resetDialog();
      } else if (uitkomst === "geen_gehoor") {
        if (extra.laat_afvallen) {
          toast.success("Verkoopkans afgevallen");
          setOpen(false); resetDialog();
        } else if ((data.geen_gehoor_count || 0) >= 5) {
          setStap("afval"); // 5e keer geen gehoor → afval aanbieden
        } else {
          const wanneer = data.gepland_op
            ? new Date(data.gepland_op).toLocaleString("nl-NL", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
            : "";
          toast.success(`Geen gehoor (${data.geen_gehoor_count}×) — opnieuw ingepland: ${wanneer}`);
          setOpen(false); resetDialog();
        }
      }
    } catch { toast.error("Loggen mislukt"); }
    finally { setBusy(false); }
  };

  const planTerugbel = () => {
    const iso = new Date(`${datum}T${tijd}`).toISOString();
    log("later_terugbellen", { gepland_op: iso });
  };

  const del = async (bid) => {
    try { await api.delete(`/belletjes/${bid}`); load(); }
    catch { toast.error("Verwijderen mislukt"); }
  };

  const geenGehoorTotaal = items.filter((b) => b.uitkomst === "geen_gehoor").length;

  return (
    <div className="bg-card border border-border rounded-xl p-5 space-y-3" data-testid="belletjes-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PhoneCall className="h-4 w-4 text-primary" />
          <span className="text-xs uppercase text-muted-foreground tracking-wide">Belletjes</span>
          <Badge variant="secondary" data-testid="belletje-count">{items.length}</Badge>
        </div>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="log-belletje-btn">
          <PhoneCall className="h-4 w-4 mr-1" /> Belletje loggen
        </Button>
      </div>

      <div className="space-y-2 max-h-72 overflow-y-auto">
        {items.map((b) => (
          <div key={b.id} className="border border-border rounded-md p-2.5 bg-secondary/30 group" data-testid={`belletje-row-${b.id}`}>
            <div className="flex items-center justify-between gap-2">
              <Badge variant={VARIANT[b.uitkomst] || "outline"} className="text-xs">
                {b.uitkomst === "deal" && <Trophy className="h-3 w-3 mr-1" />}
                {LABELS[b.uitkomst] || b.uitkomst}
              </Badge>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{fmtDateTime(b.created_at)}</span>
                <button onClick={() => del(b.id)}
                  className="text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`belletje-delete-${b.id}`} title="Verwijderen">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            {b.notitie && <div className="text-sm mt-1 whitespace-pre-wrap">{b.notitie}</div>}
            <div className="text-xs text-muted-foreground mt-1">{b.eigenaar_naam}</div>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-muted-foreground italic">Nog geen belletjes gelogd.</div>}
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetDialog(); }}>
        <DialogContent data-testid="belletje-dialog">
          <DialogHeader><DialogTitle>Belletje loggen</DialogTitle></DialogHeader>

          {stap === "kies" && (
            <div className="space-y-4">
              <div>
                <Label>Notitie</Label>
                <Textarea value={notitie} onChange={(e) => setNotitie(e.target.value)} rows={3}
                  data-testid="belletje-notitie" placeholder="Bijv. teruggebeld, sample volgende week…" autoFocus />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Kies de uitkomst <span className="text-destructive">*</span></Label>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  {UITKOMSTEN.map((u) => {
                    const Icon = u.icon;
                    return (
                      <Button key={u.value} disabled={busy}
                        onClick={() => u.value === "later_terugbellen" ? setStap("terugbel") : log(u.value)}
                        className={`justify-start ${BTN_CLASS[u.value] || ""} ${u.value === "deal" ? "col-span-2" : ""}`}
                        data-testid={`belletje-uitkomst-${u.value}`}>
                        <Icon className="h-4 w-4 mr-1" />{u.label}
                      </Button>
                    );
                  })}
                </div>
                {geenGehoorTotaal >= 4 && (
                  <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                    <AlertTriangle className="h-3.5 w-3.5" /> Al {geenGehoorTotaal}× geen gehoor — bij de 5e keer kun je de kans laten afvallen.
                  </p>
                )}
              </div>
            </div>
          )}

          {stap === "terugbel" && (
            <div className="space-y-4" data-testid="belletje-terugbel-stap">
              <p className="text-sm text-muted-foreground">Wanneer wil je terugbellen?</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Datum</Label>
                  <Input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} data-testid="belletje-terugbel-datum" /></div>
                <div><Label className="text-xs">Tijd</Label>
                  <Input type="time" step="300" value={tijd} onChange={(e) => setTijd(e.target.value)} data-testid="belletje-terugbel-tijd" /></div>
              </div>
              <div className="flex justify-between">
                <Button variant="ghost" onClick={() => setStap("kies")}>Terug</Button>
                <Button disabled={busy} onClick={planTerugbel} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="belletje-terugbel-bevestig">
                  <CalendarClock className="h-4 w-4 mr-1" /> Inplannen & loggen
                </Button>
              </div>
            </div>
          )}

          {stap === "afval" && (
            <div className="space-y-4" data-testid="belletje-afval-stap">
              <div className="rounded-lg border border-amber-400 bg-amber-50 p-3 text-sm flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
                <span><strong>5× geen gehoor.</strong> Wil je deze verkoopkans laten afvallen, of het zojuist ingeplande belmoment behouden?</span>
              </div>
              <div className="flex justify-between">
                <Button variant="outline" disabled={busy}
                  onClick={() => { toast.success("Belmoment behouden"); setOpen(false); resetDialog(); }}
                  data-testid="belletje-afval-behoud">Belmoment behouden</Button>
                <Button variant="destructive" disabled={busy}
                  onClick={async () => {
                    try {
                      await api.patch(`/verkoopkansen/${kansId}`, { fase: "Afgevallen", gepland_op: null });
                      toast.success("Verkoopkans afgevallen");
                      await load(); onLogged?.(); setOpen(false); resetDialog();
                    } catch { toast.error("Mislukt"); }
                  }}
                  data-testid="belletje-afval-bevestig">Laat afvallen</Button>
              </div>
            </div>
          )}

          {stap === "kies" && (
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)} data-testid="belletje-sluiten">Sluiten</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
